import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, AlertTriangle, Check, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../lib/api';

/**
 * Page de bootstrap admin — accessible publiquement mais protégée par un token.
 * L'utilisateur entre :
 *   1. Son email (avec lequel il s'est inscrit)
 *   2. Le token ADMIN_BOOTSTRAP_TOKEN défini dans les env vars Emergent
 * → son compte est promu admin instantanément.
 *
 * Désactivé automatiquement côté backend si ADMIN_BOOTSTRAP_TOKEN n'est pas défini en env.
 */
const Bootstrap = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);
    setLoading(true);
    try {
      const res = await api.post('/auth/bootstrap-admin',
        { email: email.trim().toLowerCase() },
        { headers: { 'X-Bootstrap-Token': token.trim() } }
      );
      setResult(res.data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || 'Erreur inconnue';
      if (status === 503) setError("L'endpoint n'est pas activé. Vous devez d'abord ajouter la variable ADMIN_BOOTSTRAP_TOKEN dans les env vars Emergent et redéployer.");
      else if (status === 401) setError("Token incorrect. Vérifiez que vous avez bien copié la valeur exacte de ADMIN_BOOTSTRAP_TOKEN depuis Emergent.");
      else if (status === 404) setError(`Aucun compte trouvé avec l'email "${email}". Vérifiez l'orthographe.`);
      else setError(`Erreur ${status || ''}: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] py-12 px-4">
      <div className="max-w-lg mx-auto">
        <div className="bg-white border border-[#E8E0D0] rounded-2xl p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-[#1F4D3F] flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-serif text-2xl font-semibold text-[#2D2A26]">Bootstrap Admin</h1>
              <p className="text-xs text-[#7A6F5C]">Promotion manuelle d'un compte au rôle admin</p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5 flex items-start gap-2 text-xs text-amber-900">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <strong>Page sensible.</strong> Utilisez-la une seule fois pour devenir admin, puis retirez la variable <code>ADMIN_BOOTSTRAP_TOKEN</code> dans les env vars Emergent et redéployez pour la désactiver.
            </div>
          </div>

          {result ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4" data-testid="bootstrap-success">
              <div className="flex items-center gap-2 text-emerald-900 font-medium mb-2">
                <Check className="w-5 h-5" /> Succès !
              </div>
              <div className="text-sm text-emerald-900 space-y-1">
                <p>Le compte <strong>{result.email}</strong> est maintenant <strong>admin</strong>{result.previous_role ? <> (avant : {result.previous_role})</> : null}.</p>
                {result.changed === false && <p className="text-xs italic">Ce compte était déjà admin — aucune modification.</p>}
                <p className="mt-3">
                  <strong>Étape suivante :</strong> Déconnectez-vous, reconnectez-vous, puis allez sur la page Admin.
                </p>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={() => navigate('/connexion')} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white">
                  Se reconnecter
                </Button>
                <Button variant="outline" onClick={() => navigate('/admin')}>
                  Aller sur /admin
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="bootstrap-form">
              <div>
                <label className="text-sm text-[#2D2A26] mb-1.5 block">
                  Votre email de connexion
                </label>
                <Input
                  type="email" required value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="vous@exemple.com"
                  data-testid="bootstrap-input-email"
                  autoComplete="email"
                />
                <p className="text-[11px] text-[#7A6F5C] mt-1">
                  L'email exact que vous avez utilisé pour vous inscrire sur cliniqpros.com
                </p>
              </div>

              <div>
                <label className="text-sm text-[#2D2A26] mb-1.5 block">
                  Token secret <code className="text-[10px] text-[#7A6F5C]">ADMIN_BOOTSTRAP_TOKEN</code>
                </label>
                <Input
                  type="password" required value={token} onChange={e => setToken(e.target.value)}
                  placeholder="Collez ici la valeur définie dans les env vars Emergent"
                  data-testid="bootstrap-input-token"
                  autoComplete="off"
                />
                <p className="text-[11px] text-[#7A6F5C] mt-1">
                  Cette valeur doit avoir été ajoutée préalablement dans <strong>Emergent → Settings → Environment Variables</strong> avec le nom exact <code>ADMIN_BOOTSTRAP_TOKEN</code>.
                </p>
              </div>

              {error && (
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-900" data-testid="bootstrap-error">
                  {error}
                </div>
              )}

              <Button type="submit" disabled={loading || !email || !token}
                data-testid="bootstrap-submit"
                className="w-full bg-[#1F4D3F] hover:bg-[#173B30] text-white">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Promotion en cours…</> : 'Promouvoir mon compte en admin'}
              </Button>
            </form>
          )}
        </div>

        <details className="mt-6 bg-white border border-[#E8E0D0] rounded-xl p-4 text-sm" data-testid="bootstrap-help">
          <summary className="cursor-pointer font-medium text-[#1F4D3F]">Comment générer le token ADMIN_BOOTSTRAP_TOKEN ?</summary>
          <div className="mt-3 space-y-3 text-[#5A5247]">
            <p><strong>Étape 1.</strong> Allez sur <a href="https://www.uuidgenerator.net/" target="_blank" rel="noopener noreferrer" className="text-[#1F4D3F] underline">uuidgenerator.net</a> et copiez un UUID généré (n'importe quelle longue chaîne aléatoire fonctionne).</p>
            <p><strong>Étape 2.</strong> Dans Emergent → Settings → Environment Variables → Backend, ajoutez :</p>
            <pre className="bg-[#2D2A26] text-[#A8C5B5] rounded p-2 text-xs">ADMIN_BOOTSTRAP_TOKEN = (collez votre UUID)</pre>
            <p><strong>Étape 3.</strong> Cliquez sur <strong>Deploy</strong> dans Emergent et attendez ~2 minutes.</p>
            <p><strong>Étape 4.</strong> Revenez sur cette page et entrez votre email + le même UUID dans le champ token.</p>
            <p><strong>Étape 5 (sécurité)</strong>: une fois admin, retournez dans Emergent et <strong>supprimez</strong> la variable <code>ADMIN_BOOTSTRAP_TOKEN</code>, puis redéployez. Cette page redeviendra inutilisable.</p>
          </div>
        </details>
      </div>
    </div>
  );
};

export default Bootstrap;
